import { Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { AbstractControl, FormArray, FormControl, FormGroup, NG_VALUE_ACCESSOR } from "@angular/forms";

import { BaseFormControlComponent } from "../../../../shared-library/form-controls/base-form-control.component";
import { DestroyableMixin } from "../../../../shared-library/mixins/destroyable.mixin";
import { AggregationItemModel } from "../../directory-search/models/aggregation-item.model";
import { SearchTabConfigModel } from "../../directory-search/models/search-tab-config.model";

@Component({
    selector: "pcg-ng-multiple-choice-facet",
    templateUrl: "./multiple-choice-facet.component.html",
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MultipleChoiceFacetComponent), multi: true }
    ]
})
export class MultipleChoiceFacetComponent extends DestroyableMixin(class extends BaseFormControlComponent<Array<string>> {
}) implements OnInit, OnChanges, OnDestroy {
    @Input()
    public aggregation: AggregationItemModel;

    @Input()
    public allowMultipleChoice: boolean = true;

    @Input()
    public searchConfig: SearchTabConfigModel;

    public checkboxesList: FormArray = new FormArray([]);

    public constructor() {
        super();
    }

    public ngOnInit(): void {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.buildCheckboxesList(changes.aggregation.currentValue);
    }

    public writeValue(value: any): void {
        super.writeValue(value);
        this.setSelectedCheckboxes();
    }

    public castToFormControl(control: AbstractControl): FormControl {
        return control as FormControl;
    }

    public castToFormGroup(control: AbstractControl): FormGroup {
        return control as FormGroup;
    }

    public getCheckboxTitle(value: string): string {
        const aggItem = this.aggregation.subcategories.find(item => item.value === value);
        const title = aggItem ? `${aggItem.title} (${aggItem.count})` : "";

        return title;
    }

    private buildCheckboxesList(aggregation: AggregationItemModel): void {
        this.checkboxesList.clear();

        aggregation.subcategories.forEach((item) => {
            const checkboxControl = new FormGroup({
                isSelected: new FormControl(false),
                key: new FormControl(item.value)
            });

            // TODO-VTA: store subscriptions in array and dispose it
            checkboxControl.valueChanges.subscribe((value: { isSelected: boolean, key: string, }) => {
                const newValue = this.value.slice();
                if (value.isSelected) {
                    newValue.push(value.key);
                } else {
                    newValue.splice(newValue.indexOf(value.key), 1);
                }
                this.writeNewValue(newValue);
            });

            this.checkboxesList.push(checkboxControl);
        }
        );

        this.setSelectedCheckboxes();
    }

    private setSelectedCheckboxes(): void {
        if (this.value && this.value.length) {
            for (const checkbox of this.checkboxesList.controls.map(c => c as FormGroup)) {
                const isSelected = this.value.indexOf(checkbox.controls.key.value) > -1;
                checkbox.controls.isSelected.setValue(isSelected, { emitEvent: false });
            }
        }
    }

    public ngOnDestroy(): void {
        this.destroy();
    }

    public hasFilterInGroup(category: AggregationItemModel): boolean {
        return category.subcategories?.some(x => x.selected || this.hasFilterInGroup(x)) ?? false;
    }

    public selectedFiltersInGroup(category: AggregationItemModel): string {
        const titles = this.collectSelectedTitlesRecursive(category);

        return titles.join(", ");
    }

    private collectSelectedTitlesRecursive(category: AggregationItemModel): Array<string> {
        let results: Array<string> = [];

        if (category.subcategories) {
            category.subcategories.forEach((sub) => {
                if (sub.selected) {
                    results.push(sub.title);
                }

                if (sub.subcategories && sub.subcategories.length > 0) {
                    results = results.concat(this.collectSelectedTitlesRecursive(sub));
                }
            });
        }

        return results;
    }

    public onCategoriesGroupItemClicked(e: Event, category: AggregationItemModel): void {
        e.preventDefault();

        const parentElement = (e.target as any).closest(".pcg-filter-collapsible");
        const isExpanded = parentElement.classList.contains("expanded");

        if (isExpanded) {
            parentElement.classList.remove("expanded");
            (e.target as any).ariaExpanded = false;

            category.expanded = false;
        } else {
            parentElement.classList.add("expanded");
            (e.target as any).ariaExpanded = true;

            category.expanded = true;
        }
    }

    public onCategoryItemClicked(e: Event): void {
        const categoryId = (e.target as any).value;
        let selectedCategories = this.value || [];
        const categoryIndex = selectedCategories.findIndex(id => (id === categoryId));

        if (this.allowMultipleChoice) {
            if (categoryIndex > -1) {
                selectedCategories.splice(categoryIndex, 1);
            } else {
                selectedCategories.push(categoryId);
            }
        } else {
            selectedCategories = [];

            if (categoryIndex === -1) {
                selectedCategories.push(categoryId);
            }
        }

        this.aggregation.subcategories.forEach((category) => {
            category.selected = (selectedCategories.indexOf(category.value) > -1);

            if (category.subcategories) {
                category.subcategories.forEach((subcategory) => {
                    subcategory.selected = (selectedCategories.indexOf(subcategory.value) > -1);
                });
            }
        });

        this.writeNewValue(selectedCategories);
    }
}
